// src/layout/common/MasterDataDisplay.jsx
import React, { useEffect } from "react";
import useProjectInfoStore from "../../store/builders/useProjectInfoStore"; // Reusing this store
import { ClipLoader } from "react-spinners";
import {
  BuildingOffice2Icon,
  UserCircleIcon,
  MapPinIcon,
  PhoneIcon,
  AtSymbolIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  DocumentTextIcon, // Renamed from ArchiveBoxIcon
  ArrowUpRightIcon, // For document links
  ClockIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  // LinkIcon, // Not used in current setup
  ChatBubbleBottomCenterTextIcon, // For enquiry comments
} from "@heroicons/react/24/outline";

// Helper to format date and time
const formatDateTime = (dateString, includeTime = false) => {
  if (!dateString) return "N/A";
  try {
    const options = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    if (includeTime) {
      options.hour = "2-digit";
      options.minute = "2-digit";
      options.hour12 = true;
    }
    return new Date(dateString).toLocaleDateString("en-GB", options);
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return "Invalid Date";
  }
};

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatStatus = (status) => {
  if (!status) return "N/A";
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const InfoItem = ({ icon: Icon, label, value, children, className = "", isMultiline = false }) => (
  <div className={`flex items-start space-x-3 ${className}`}>
    {Icon && <Icon className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />}
    <div>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <div className={`text-sm text-gray-800 mt-0.5 ${isMultiline ? 'whitespace-pre-wrap' : ''}`}>
        {value !== undefined ? value : children || "N/A"}
      </div>
    </div>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white shadow rounded-lg p-5 mb-6 border border-gray-100">
    <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 flex items-center">
      {Icon && <Icon className="w-5 h-5 mr-2 text-blue-600" />}
      {title}
    </h2>
    {children}
  </div>
);

const DocumentLink = ({ url, label }) => {
  if (!url) {
    return <span className="text-gray-400 italic text-xs">Not Uploaded</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800 hover:underline text-xs inline-flex items-center"
    >
      {label}
      <ArrowUpRightIcon className="w-3 h-3 ml-1" />
    </a>
  );
};

const MasterDataDisplay = ({ projectId }) => {
  const {
    masterData,
    fetchProjectMasterData,
    isLoading,
    error,
  } = useProjectInfoStore();

  useEffect(() => {
    if (projectId) {
      fetchProjectMasterData(projectId);
    }
  }, [projectId, fetchProjectMasterData]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow">
        <ClipLoader size={40} color={"#3B82F6"} />
        <p className="ml-3 text-gray-600">Loading Project Data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-6 text-red-600 bg-red-50 rounded-lg shadow border border-red-200">
        <ExclamationTriangleIcon className="w-10 h-10 mx-auto mb-2 text-red-500" />
        <p className="font-semibold">Error loading project data:</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!masterData) {
    return (
      <div className="text-center p-6 text-gray-500 bg-white rounded-lg shadow">
        No project data available.
      </div>
    );
  }

  const { project, customer, builder, messages } = masterData;
  const { holds, payments, documents, invitation, enquiry, enquiryComments } = project || {}; // Destructure enquiryComments

  return (
    <div className="space-y-6">
      <Section title="Project Overview" icon={ClipboardDocumentListIcon}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoItem label="Project Name" value={project?.project_name} className="lg:col-span-2" />
          <InfoItem label="Status" value={formatStatus(project?.status)} />
          <InfoItem label="Location" value={project?.project_location} icon={MapPinIcon} className="lg:col-span-2" />
          <InfoItem label="Budget" value={formatCurrency(project?.project_budget)} icon={CurrencyRupeeIcon} />
          <InfoItem label="Start Date" value={formatDateTime(project?.starting_date)} icon={CalendarDaysIcon} />
          <InfoItem label="Deadline" value={formatDateTime(project?.deadline_date)} icon={CalendarDaysIcon} />
          <InfoItem label="Completed Date" value={formatDateTime(project?.completed_date)} icon={CalendarDaysIcon} />
          <InfoItem label="Created On" value={formatDateTime(project?.created_at, true)} icon={ClockIcon} />
          <InfoItem label="Last Updated" value={formatDateTime(project?.updated_at, true)} icon={ClockIcon} />
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Builder Information" icon={BuildingOffice2Icon}>
          <div className="space-y-3">
            <InfoItem label="Company" value={builder?.company} />
            <InfoItem label="Engineer" value={builder?.name} />
            <InfoItem label="Ducktail ID" value={builder?.builder_id} icon={IdentificationIcon} />
            <InfoItem label="Contact" value={builder?.contact} icon={PhoneIcon} />
            <InfoItem label="Email" value={builder?.email} icon={AtSymbolIcon} />
            {builder?.image && (
              <InfoItem label="Profile Image">
                <img src={builder.image} alt="Builder Profile" className="w-16 h-16 rounded-md object-cover mt-1 border" />
              </InfoItem>
            )}
          </div>
        </Section>

        <Section title="Customer Information" icon={UserCircleIcon}>
          <div className="space-y-3">
            <InfoItem label="Name" value={customer?.name} />
            <InfoItem label="Ducktail ID" value={customer?.customer_id} icon={IdentificationIcon} />
            <InfoItem label="Email" value={customer?.email} icon={AtSymbolIcon} />
            <InfoItem label="Address" icon={MapPinIcon}>
              {customer?.address ? (
                <>
                  {customer.address.address_line1}
                  {customer.address.address_line2 && `, ${customer.address.address_line2}`}
                  <br />
                  {customer.address.taluk}, {customer.address.district}
                  <br />
                  {customer.address.state} - {customer.address.postcode}
                  {customer.address.latitude && customer.address.longitude && (
                    <span className="text-xs block text-gray-400 mt-1">
                      (Lat: {customer.address.latitude}, Lng: {customer.address.longitude})
                    </span>
                  )}
                </>
              ) : "N/A"}
            </InfoItem>
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {enquiry && (
          <Section title="Original Enquiry Details" icon={ClipboardDocumentListIcon}>
            <div className="space-y-3">
              <InfoItem label="Enquirer Name" value={enquiry.customer_name} />
              <InfoItem label="Phone" value={enquiry.phone_number} />
              <InfoItem label="Location" value={`${enquiry.taluk}, ${enquiry.district}, ${enquiry.state} - ${enquiry.postcode}`} icon={MapPinIcon} />
              <InfoItem label="Service ID" value={enquiry.service_id} />
              <InfoItem label="Primary Comment" value={enquiry.comments} isMultiline={true} /> {/* Display primary comment */}
              <InfoItem label="Status" value={formatStatus(enquiry.status)} />
              <InfoItem label="Enquired On" value={formatDateTime(enquiry.created_at, true)} icon={ClockIcon} />
              <InfoItem label="Last Action On" value={formatDateTime(enquiry.updated_at, true)} icon={ClockIcon} />
            </div>
          </Section>
        )}

        {invitation && (
          <Section title="Project Invitation" icon={DocumentTextIcon}> {/* Changed icon */}
            <div className="space-y-3">
              <InfoItem label="Status" value={formatStatus(invitation.status)} />
              <InfoItem label="Service" value={invitation.service} />
              <InfoItem label="Location" value={`${invitation.taluk}, ${invitation.district} - ${invitation.postal_code}`} icon={MapPinIcon} />
              <InfoItem label="Invited On" value={formatDateTime(invitation.created_at, true)} icon={ClockIcon} />
            </div>
          </Section>
        )}
      </div>

      {/* --- NEW: Enquiry Comments History Section --- */}
      {enquiryComments && enquiryComments.length > 0 && (
        <Section title="Enquiry Comment History" icon={ChatBubbleBottomCenterTextIcon}>
          <ul className="space-y-3 divide-y divide-gray-100 max-h-60 overflow-y-auto pr-2">
            {enquiryComments.map((comment) => (
              <li key={comment.id} className="pt-3 first:pt-0">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDateTime(comment.created_at, true)}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {/* --- END NEW SECTION --- */}


      {documents && (
        <Section title="Completion Documents" icon={DocumentTextIcon}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <InfoItem label="Specification Report"><DocumentLink url={documents.specification_report_url} label="View/Download" /></InfoItem>
            <InfoItem label="Warranty Document"><DocumentLink url={documents.warranty_document_url} label="View/Download" /></InfoItem>
            <InfoItem label="Completion Certificate"><DocumentLink url={documents.completion_certificate_url} label="View/Download" /></InfoItem>
            <InfoItem label="Site Image">
              {documents.site_image_url ? (
                <a href={documents.site_image_url} target="_blank" rel="noopener noreferrer">
                  <img src={documents.site_image_url} alt="Site" className="w-24 h-24 rounded-md object-cover mt-1 border hover:opacity-80 transition-opacity" />
                </a>
              ) : <span className="text-gray-400 italic text-xs">Not Uploaded</span>}
            </InfoItem>
          </div>
        </Section>
      )}

      {holds && holds.length > 0 && (
        <Section title="Hold History" icon={ClockIcon}>
          <ul className="space-y-3 divide-y divide-gray-100">
            {holds.map((hold) => (
              <li key={hold.id} className="pt-3 first:pt-0">
                <p className="text-sm text-gray-700">{hold.comment}</p>
                <p className="text-xs text-gray-500 mt-1">{formatDateTime(hold.created_at, true)}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {payments && payments.length > 0 && (
        <Section title="Payment History" icon={CreditCardIcon}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="py-2 px-3 text-left font-medium">Date</th>
                  <th className="py-2 px-3 text-left font-medium">Amount</th>
                  <th className="py-2 px-3 text-left font-medium">Status</th>
                  <th className="py-2 px-3 text-left font-medium">Comments</th>
                  <th className="py-2 px-3 text-left font-medium">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50/50">
                    <td className="py-2 px-3 whitespace-nowrap">{formatDateTime(payment.created_at, true)}</td>
                    <td className="py-2 px-3 whitespace-nowrap">{formatCurrency(payment.amount)}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${payment.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {formatStatus(payment.payment_status)}
                      </span>
                    </td>
                    <td className="py-2 px-3">{payment.comments || "-"}</td>
                    <td className="py-2 px-3 whitespace-nowrap">{formatStatus(payment.payment_type)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {messages && messages.length > 0 && (
        <Section title="Recent Chat Messages" icon={ChatBubbleLeftRightIcon}>
          <ul className="space-y-3 divide-y divide-gray-100 max-h-60 overflow-y-auto pr-2">
            {messages.map((msg) => (
              <li key={msg.id} className="pt-3 first:pt-0">
                <p className="text-sm text-gray-700">{msg.message}</p>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="font-medium">{msg.sender_id === builder?.id ? "Builder" : "Customer"}</span> - {formatDateTime(msg.created_at, true)}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

export default MasterDataDisplay;